import { useState, useEffect, useCallback } from 'react';
import { getStoreSettings } from '../firebase/settings-functions';
import { DEFAULT_STORE_ID } from '../firebase/firebase';
import { useLanguage } from '../context/LanguageContext';
import { CacheManager } from '../utils/cacheManager';

export const useStoreSettings = (storeId = DEFAULT_STORE_ID) => {
    const [storeSettings, setStoreSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { language } = useLanguage();

    const fetchStoreSettings = useCallback(async () => {
        if (!storeId) {
            setLoading(false);
            return;
        }

        // Try to get cached settings first
        const cacheKey = `${CacheManager.CACHE_KEYS.STORE_SETTINGS}_${storeId}_${language}`;
        const cachedSettings = CacheManager.get(cacheKey, CacheManager.EXPIRATION.STORE_SETTINGS);

        if (cachedSettings) {
            setStoreSettings(cachedSettings);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const settings = await getStoreSettings(storeId, { language, simplified: true });
            setStoreSettings(settings);

            // Cache the settings
            CacheManager.set(cacheKey, settings);
        } catch (err) {
            console.error('Error fetching store settings:', err);
            setError(err);
            // Set default settings on error
            setStoreSettings({
                currency: 'AED',
                name: '',
                domain: '',
                taxRate: 0,
                // ... other defaults
            });
        } finally {
            setLoading(false);
        }
    }, [storeId, language]);

    useEffect(() => {
        fetchStoreSettings();
    }, [fetchStoreSettings]);

    return {
        storeSettings,
        loading,
        error,
        currency: storeSettings?.currency || '',
        domain: storeSettings?.domain || '',
        refreshSettings: fetchStoreSettings
    };
};
